import React, { Dispatch, SetStateAction } from 'react';
import {
	Box,
	FormControlLabel,
	useTheme,
	Checkbox,
	Typography,
} from '@mui/material';
import { useQuoteState } from 'src/stateProviders/QuoteStateProvider';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import CheckIcon from '@mui/icons-material/Check';

const OptionsCards: React.FC<{
	selectedOptions: number[];
	setSelectedOptions: Dispatch<SetStateAction<number[]>>;
}> = ({ selectedOptions, setSelectedOptions }) => {
	const { options } = useQuoteState();
	const theme = useTheme();

	return (
		<Box
			sx={{
				width: '100%',
				display: 'flex',
				justifyContent: 'space-between',
				alignItems: 'center',
				gap: '15px',
				marginY: '10px',
			}}
		>
			{options?.map((option, idx) => {
				const isSelected = selectedOptions.includes(idx);
				const cardBg = isSelected
					? theme.palette.primary.main
					: theme.palette.background.paper;
				const cardColor = isSelected
					? theme.palette.background.paper
					: theme.palette.text.primary;

				return (
					<Box
						key={idx}
						onClick={() =>
							setSelectedOptions((prev) => {
								if (isSelected) {
									return prev.filter((id) => id != idx);
								} else {
									return [...prev, idx];
								}
							})
						}
						sx={{
							width: 'calc((100% / 3) - 10px)',
							height: '90px',
							display: 'flex',
							flexDirection: 'column',
							justifyContent: 'center',
							alignItems: 'flex-start',
							padding: '10px',
							cursor: 'pointer',
							bgcolor: cardBg,
							color: cardColor,
							transition: theme.transitions.create(
								['background-color', 'box-shadow'],
								{
									duration: theme.transitions.duration.short,
								}
							),
							borderRadius: '8px',
							border: isSelected
								? `2px solid ${theme.palette.primary.main}`
								: `1px solid ${theme.palette.divider}`,
						}}
					>
						<FormControlLabel
							value={idx}
							control={
								<Checkbox
									checked={isSelected}
									icon={<RadioButtonUncheckedIcon />}
									checkedIcon={
										<span
											style={{
												width: 20,
												height: 20,
												borderRadius: '50%',
												border: '2px solid white',
												backgroundColor: 'white',
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
											}}
										>
											<CheckIcon
												style={{
													fontSize: 16,
													color: theme.palette.primary.main,
													fontWeight: 'bold',
												}}
											/>
										</span>
									}
									sx={{
										'&.Mui-checked': {
											color: theme.palette.background.paper,
										},
									}}
								/>
							}
							label={`Option ${idx + 1}`}
							sx={{
								width: '100%',
								'& .MuiFormControlLabel-label': {
									fontWeight: 600,
									color: isSelected
										? theme.palette.background.paper
										: theme.palette.text.primary,
								},
							}}
						/>

						<Typography
							sx={{
								width: '100%',
								paddingLeft: '30px',
								fontSize: '12px',
								color: isSelected
									? theme.palette.background.paper
									: theme.palette.text.secondary,
							}}
						>
							{option.deckingType || '---'}
						</Typography>

						<Typography
							sx={{
								width: '100%',
								paddingLeft: '30px',
								fontSize: '12px',
								color: isSelected
									? theme.palette.background.paper
									: theme.palette.text.secondary,
							}}
						>
							{option.railingType || '---'}{' '}
							{option.railCapStyle ? `, ${option.railCapStyle}` : ''}
						</Typography>
					</Box>
				);
			})}
		</Box>
	);
};

export default OptionsCards;
